import { useState, useCallback } from 'react';

import { ApiKeysService } from '@app/api/api-keys';
import { ApiKey, CreateKeyResponse } from '@app/api/api-keys/types';

export function useKeyManagement() {
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [keyLimit, setKeyLimit] = useState<number>(20);
  const [isLoading, setIsLoading] = useState(true);

  const fetchKeys = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await ApiKeysService.list();

      // Ensure data.keys is always an array
      if ('keys' in data && Array.isArray(data.keys)) {
        setKeys(data.keys);
        setKeyLimit(data.active_key_limit ?? 20);
      } else {
        setKeys(Array.isArray(data) ? data : []);
        console.error('Unexpected API response format:', data);
      }
    } catch (error) {
      console.error('Error fetching keys:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const createKey = useCallback(async (formData: CreateKeyForm): Promise<CreateKeyResponse> => {
    const data = await ApiKeysService.create({
      name: formData.name,
      expires_at: formData.expires_at == null ? undefined : new Date(formData.expires_at),
    });

    // Update the keys list with the new key (minus the secret)
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { secret, ...newKey } = data;
    setKeys((currentKeys) => [...currentKeys, newKey]);

    return data;
  }, []);

  return {
    keys,
    keyLimit,
    isLoading,
    createKey,
    fetchKeys,
    hasReachedLimit: keys.length >= keyLimit,
  };
}

export interface CreateKeyForm {
  name: string;
  expires_at: string | null;
}
